/**
 * @fileoverview Edit mode content for patient details modal
 */
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { intl, DmsSelect, DmsTextField, DmsCheckBox, DmsTextFieldPhone } from "dmsbasereact";
import { DmsAutocomplete } from "../../DmsAutoComplete/DmsAutoComplete";
import {
  Box, Typography, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ContactsIcon from "@mui/icons-material/Contacts";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { DmsInfoCard } from "../../DmsDetailModal/DmsInfoCard";
import { getBrazilianStateOptions } from "../../../constants/BrazilianStates";
import DmsSelectRGIssuingAgency from "../../DmsSelectRGIssuingAgency/DmsSelectRGIssuingAgency";
import { normalizeGender } from "../../../utils/formatters";
import { PatientStatusEnum } from "../../../constants/PatientStatusEnum";
import { DmsTextFieldCep } from "../../DmsTextFieldCep";

/**
 * Edit mode content for editing patient details
 */
export const EditModeContent = ({ data,
  formData,
  onFieldChange,
  patientStatusReasons = [],
  originalPatient = null}) => {
  const patient = data;

  // Ensure gender is normalized
  const genderValue = normalizeGender(formData.gender);

  // Check if status is changing
  const statusChanging = originalPatient && formData.status !== originalPatient.status;

  // Find "Other" reason option
  const otherReason = useMemo(() => {
    return patientStatusReasons.find(r => r.name && r.name.toUpperCase() === "OTHER");
  }, [patientStatusReasons]);

  // Check if selected reason is "Other"
  const isOtherSelected = useMemo(() => {
    if (!formData.patient_status_reason_pk || !otherReason) return false;
    return Number(formData.patient_status_reason_pk) === Number(otherReason.pk);
  }, [formData.patient_status_reason_pk, otherReason]);

  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
      {/* Basic Information */}
      <DmsInfoCard icon={<PersonIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">{intl.get("Number").d("Number")}</Typography>
            <Typography variant="body1">{patient.patient_identifier}</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={intl.get("Name").d("Name")}
              value={formData.name || ""}
              onChange={(e) => onFieldChange("name", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={intl.get("CPF").d("CPF")}
              value={formData.cpf || ""}
              fullWidth
              size="small"
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("Gender").d("Gender")}</InputLabel>
              <Select
                value={genderValue}
                label={intl.get("Gender").d("Gender")}
                onChange={(e) => onFieldChange("gender", e.target.value)}
              >
                <MenuItem value="M">{intl.get("Male").d("Male")}</MenuItem>
                <MenuItem value="F">{intl.get("Female").d("Female")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={intl.get("BirthDate").d("Birth Date")}
              value={formData.birth_date || ""}
              onChange={(e) => onFieldChange("birth_date", e.target.value)}
              fullWidth
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("Status").d("Status")}</InputLabel>
              <Select
                value={formData.status ?? PatientStatusEnum.ACTIVE}
                label={intl.get("Status").d("Status")}
                onChange={(e) => {
                  onFieldChange("status", Number(e.target.value));
                  if (originalPatient && Number(e.target.value) === originalPatient.status) {
                    onFieldChange("patient_status_reason_pk", null);
                    onFieldChange("status_reason", "");
                  }
                }}
              >
                <MenuItem value={PatientStatusEnum.ACTIVE}>{intl.get("Active").d("Active")}</MenuItem>
                <MenuItem value={PatientStatusEnum.INACTIVE}>{intl.get("Inactive").d("Inactive")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* Status Change Reason - Show when status is changing */}
      {statusChanging && (
        <DmsInfoCard icon={<PersonIcon />} title={intl.get("StatusChangeReason").d("Status Change Reason")}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DmsSelect
                label={intl.get("StatusReason").d("Status Reason")}
                value={formData.patient_status_reason_pk || ""}
                onChange={(value) => {
                  onFieldChange("patient_status_reason_pk", value);
                  const selectedReason = patientStatusReasons.find(r => r.pk === Number(value));
                  if (!selectedReason || selectedReason.name.toUpperCase() !== "OTHER") {
                    onFieldChange("status_reason", "");
                  }
                }}
                options={patientStatusReasons
                  .filter(r => r && r.pk && r.name)
                  .map(r => ({ value: r.pk, label: intl.get(`PatientStatusReason.${r.name}`).d(r.description || r.name) }))}
                fullWidth
                required
                sx={{ mb: isOtherSelected ? 2 : 0 }}
              />
              {isOtherSelected && (
                <DmsTextField
                  label={intl.get("CustomReason").d("Custom Reason")}
                  value={formData.status_reason || ""}
                  onChange={(e) => onFieldChange("status_reason", e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  required
                  placeholder={intl.get("EnterCustomReasonPlaceholder").d("Enter the custom reason...")}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
          </Grid>
        </DmsInfoCard>
      )}

      {/* Contact Information */}
      <DmsInfoCard icon={<ContactsIcon />} title={intl.get("ContactInfo").d("Contact Information")}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={intl.get("Email").d("Email")}
              value={formData.email || ""}
              onChange={(e) => onFieldChange("email", e.target.value)}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid item xs={6}>
            <DmsTextFieldPhone
              label={intl.get("Phone").d("Phone")}
              value={formData.phone || ""}
              onChange={(e) => onFieldChange("phone", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* Medical Information - only for female patients */}
      {genderValue === "F" && (
        <DmsInfoCard icon={<LocalHospitalIcon />} title={intl.get("MedicalInfo").d("Medical Information")}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DmsCheckBox
                label={intl.get("FertilityPotencial").d("Fertility Potential")}
                checked={formData.has_pregnancy_potential || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onFieldChange("has_pregnancy_potential", isChecked);
                  if (isChecked) {
                    onFieldChange("selectedInfertilityReason", null);
                  } else {
                    onFieldChange("selectedContraceptiveMethod", null);
                    onFieldChange("menstrual_cycle_regular", false);
                  }
                }}
              />
            </Grid>
            {formData.has_pregnancy_potential && (
              <Grid item xs={12}>
                <DmsCheckBox
                  label={intl.get("MenstrualCycleRegular").d("Menstrual Cycle Regular")}
                  checked={formData.menstrual_cycle_regular || false}
                  onChange={(e) => onFieldChange("menstrual_cycle_regular", e.target.checked)}
                />
              </Grid>
            )}
            {formData.has_pregnancy_potential && (
              <Grid item xs={12}>
                <DmsAutocomplete
                  properties={{ label: 'reason', value: 'pk' }}
                  needs_authentication={true}
                  url={'/contraceptive_method/all'}
                  onChange={(selectedOption) => onFieldChange("selectedContraceptiveMethod", selectedOption)}
                  value={formData.selectedContraceptiveMethod}
                  label={intl.get('ContraceptiveMethod').d('Contraceptive Method')}
                />
              </Grid>
            )}
            {!formData.has_pregnancy_potential && (
              <Grid item xs={12}>
                <DmsAutocomplete
                  properties={{ label: 'reason', value: 'pk' }}
                  needs_authentication={true}
                  url={'/infertility_reason/all'}
                  onChange={(selectedOption) => onFieldChange("selectedInfertilityReason", selectedOption)}
                  value={formData.selectedInfertilityReason}
                  label={intl.get('InfertilityReason').d('Infertility Reason')}
                />
              </Grid>
            )}
          </Grid>
        </DmsInfoCard>
      )}

      {/* Address Information */}
      <DmsInfoCard icon={<HomeIcon />} title={intl.get("AddressInfo").d("Address Information")}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <DmsTextFieldCep
              name="zip_code"
              label={intl.get("ZipCode").d("Zip Code")}
              value={formData.zip_code || ""}
              onChange={(e) => onFieldChange("zip_code", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label={intl.get("Address").d("Address")}
              value={formData.address || ""}
              onChange={(e) => onFieldChange("address", e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={intl.get("Number").d("Number")}
              value={formData.number || ""}
              onChange={(e) => onFieldChange("number", e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={intl.get("City").d("City")}
              value={formData.city || ""}
              onChange={(e) => onFieldChange("city", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("State").d("State")}</InputLabel>
              <Select
                value={formData.state || ""}
                label={intl.get("State").d("State")}
                onChange={(e) => onFieldChange("state", e.target.value)}
              >
                {getBrazilianStateOptions().map(({ value: code, label }) => (
                  <MenuItem key={code} value={code}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* Responsible Legal Section */}
      <DmsInfoCard icon={<SupervisorAccountIcon />} title={intl.get("ResponsibleLegalInfo").d("Responsible Legal Information")}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DmsCheckBox
              label={intl.get("HasResponsibleLegal").d("Has Responsible Legal")}
              checked={formData.hasResponsibleLegal || false}
              onChange={(e) => {
                const isChecked = e.target.checked;
                onFieldChange("hasResponsibleLegal", isChecked);
                if (!isChecked) {
                  onFieldChange("responsibleLegal_name", "");
                  onFieldChange("responsibleLegal_cpf", "");
                  onFieldChange("responsibleLegal_rg", "");
                  onFieldChange("responsibleLegal_rg_issuing_agency", "");
                  onFieldChange("responsibleLegal_birth_date", "");
                  onFieldChange("responsibleLegal_email", "");
                  onFieldChange("responsibleLegal_phone", "");
                  onFieldChange("sameAddressAsPatient", false);
                }
              }}
            />
          </Grid>

          {formData.hasResponsibleLegal && (
            <>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("Name").d("Name")}
                  value={formData.responsibleLegal_name || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_name", e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("CPF").d("CPF")}
                  value={formData.responsibleLegal_cpf || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_cpf", e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={!!formData.responsibleLegal_pk}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("RG").d("RG")}
                  value={formData.responsibleLegal_rg || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_rg", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={6}>
                <DmsSelectRGIssuingAgency
                  value={formData.responsibleLegal_rg_issuing_agency || ""}
                  onChange={(value) => onFieldChange("responsibleLegal_rg_issuing_agency", value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("BirthDate").d("Birth Date")}
                  value={formData.responsibleLegal_birth_date || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_birth_date", e.target.value)}
                  fullWidth
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("Email").d("Email")}
                  value={formData.responsibleLegal_email || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_email", e.target.value)}
                  fullWidth
                  size="small"
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <DmsTextFieldPhone
                  label={intl.get("Phone").d("Phone")}
                  value={formData.responsibleLegal_phone || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_phone", e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Same address checkbox */}
              <Grid item xs={12}>
                <DmsCheckBox
                  label={intl.get("SameAddressAsPatient").d("Same address as patient")}
                  checked={formData.sameAddressAsPatient || false}
                  onChange={(e) => onFieldChange("sameAddressAsPatient", e.target.checked)}
                />
              </Grid>

              {/* Responsible Legal Address Fields - shown when not same address */}
              {!formData.sameAddressAsPatient && (
                <>
                  <Grid item xs={4}>
                    <DmsTextFieldCep
                      name="responsibleLegal_zip_code"
                      label={intl.get("ZipCode").d("Zip Code")}
                      value={formData.responsibleLegal_zip_code || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_zip_code", e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      label={intl.get("Address").d("Address")}
                      value={formData.responsibleLegal_address || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_address", e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label={intl.get("Number").d("Number")}
                      value={formData.responsibleLegal_number || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_number", e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label={intl.get("City").d("City")}
                      value={formData.responsibleLegal_city || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_city", e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{intl.get("State").d("State")}</InputLabel>
                      <Select
                        value={formData.responsibleLegal_state || ""}
                        label={intl.get("State").d("State")}
                        onChange={(e) => onFieldChange("responsibleLegal_state", e.target.value)}
                      >
                        {getBrazilianStateOptions().map(({ value: code, label }) => (
                          <MenuItem key={code} value={code}>{label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </DmsInfoCard>
    </Box>
  );
};

EditModeContent.propTypes = {
  data: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  patientStatusReasons: PropTypes.array,
  originalPatient: PropTypes.object,
};

